import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Play } from 'lucide-react'
import { meldeAenderung, registriereBeilage, registriereVerweis, type BeilageProps, type VerweisProps } from '@/lib/assistent/assistent-beilagen'
import { FundstelleDialog } from './components/FundstelleDialog'
import { KiLaufVorschlaege } from './components/KiLaufVorschlaege'

/**
 * AP-18 7.5 (Phase E) — was die **Kalkulation** in der Leiste des allgemeinen Assistenten selbst zeichnet.
 *
 * Der Server legt es als Beilage unter die Antwort (`calculation-assistant-tools.service.ts`); hier
 * steht, wie es aussieht — dieselben Teile wie in der früheren Leiste der Kalkulation:
 *
 * - `calculation.run` — der Vorschlags-Lauf mit Häkchen und „Übernehmen" (Detailblatt N 9.). Übernommen
 *   wird über die Kalkulation; die Seite lädt danach neu (`meldeAenderung`).
 * - `calculation.action` — der Knopf „Starten" und der Sprung zu den Materialanfragen. Beide führen
 *   über die Adresse in die Kalkulation: So gehen sie auch vom Menüpunkt „Assistent" aus.
 * - Verweis `fundstelle:<kalkulation>/<datei>#<seite>` — öffnet die Unterlage an ihrer Stelle.
 *
 * Angemeldet wird beim Laden dieser Datei; geladen wird sie beim Start der Anwendung (`App.tsx`) —
 * nicht erst mit der Seite der Kalkulation, sonst fehlten die Beilagen unter dem Menüpunkt „Assistent".
 */

const KENNUNG = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const kennung = (wert: unknown) => (typeof wert === 'string' && KENNUNG.test(wert) ? wert : null)
/** Fähigkeiten, die ihren eigenen Dialog haben; die übrigen öffnen die Aufträge für die eigene KI. */
const MIT_DIALOG = ['V6', 'CHECK']
const FAEHIGKEITEN = ['READ', 'V3', 'V4', ...MIT_DIALOG]

function LaufBeilage({ daten }: BeilageProps) {
  const kalkulationId = kennung(daten.calculationId)
  const runId = kennung(daten.runId)
  if (!kalkulationId || !runId) return null
  // Ob die Fassung noch beschreibbar ist, weiß die Kalkulation: Sie lehnt eine Übernahme sonst mit ihrem Grund ab.
  return <KiLaufVorschlaege kalkulationId={kalkulationId} runId={runId} beschreibbar onGeaendert={() => meldeAenderung('calculation', kalkulationId)} />
}

function AktionBeilage({ daten }: BeilageProps) {
  const { t } = useTranslation()
  const kalkulationId = kennung(daten.calculationId)
  const label = typeof daten.label === 'string' ? daten.label : ''
  if (!kalkulationId || !label) return null
  if (daten.type === 'OPEN_REQUESTS') {
    return (
      <Link to={`/calculations/material-requests?tab=material&calculationId=${kalkulationId}`} className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs hover:bg-accent">
        {label}
      </Link>
    )
  }
  const faehigkeit = typeof daten.capability === 'string' && FAEHIGKEITEN.includes(daten.capability) ? daten.capability : null
  if (daten.type !== 'START_TASK' || !faehigkeit) return null
  // Gestartet wird in der Kalkulation — dort steht der Dialog mit Schätzung und Bestätigung (N 8.2).
  const ziel = MIT_DIALOG.includes(faehigkeit) ? `/calculations/${kalkulationId}?kilauf=${faehigkeit}` : `/calculations/${kalkulationId}?tab=dokumente&ki=${faehigkeit}`
  return (
    <Link to={ziel} className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs text-primary-foreground">
      <Play className="h-3.5 w-3.5" /> {t('assistant.calculation.start', { label })}
    </Link>
  )
}

function FundstelleVerweis({ ziel, children }: VerweisProps) {
  const [offen, setOffen] = useState(false)
  const m = /^([0-9a-f-]{36})\/([0-9a-f-]{36})(?:#(\d+))?$/i.exec(ziel)
  if (!m || !kennung(m[1]) || !kennung(m[2])) return <>{children}</>
  return (
    <>
      <button type="button" className="text-primary hover:underline" onClick={() => setOffen(true)}>
        {children}
      </button>
      {offen && <FundstelleDialog kalkulationId={m[1]} fileId={m[2]} seite={Number(m[3] ?? 1)} onClose={() => setOffen(false)} />}
    </>
  )
}

registriereBeilage('calculation.run', LaufBeilage)
registriereBeilage('calculation.action', AktionBeilage)
registriereVerweis('fundstelle', FundstelleVerweis)
